import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ListBrowser from '@ts-private/list-browser';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useListBrowserFunctions } from 'hooks/useListBrowserFunctions';
import { FakeText, Typography } from '@totalsoft/rocket-ui';
import { RoundedHeaderTableRow, ThemedHeaderTableCell } from 'components/common/TableStyles';

const BrowserTableContainer = styled(Box)(() => ({
  width: '100%',
  overflowX: 'auto'
}));

const BrowserTable = styled(Table, { shouldForwardProp: prop => !['layout', 'minWidth'].includes(prop) })(
  ({ theme, layout, minWidth }) => ({
    marginBlock: theme.spacing(2),
    tableLayout: layout || 'auto',
    minWidth: minWidth || 'auto',
    '& .MuiTableBody-root td': {
      paddingBlock: theme.spacing(1.25)
    }
  })
);

const LoadingTableCell = styled(TableCell)(({ theme }) => ({
  border: 'none',
  paddingInline: theme.spacing(0.5)
}));

const TableItemsContainerComponent = ({ layout, minWidth, children }) => {
  const headerChild = useMemo(() => {
    if (!Array.isArray(children)) return null;
    return children[0];
  }, [children]);

  const itemChildren = useMemo(() => {
    if (!Array.isArray(children)) return null;
    return children.slice(1);
  }, [children]);

  return (
    <BrowserTableContainer>
      <BrowserTable layout={layout} minWidth={minWidth}>
        {headerChild}
        <TableBody>{itemChildren}</TableBody>
      </BrowserTable>
    </BrowserTableContainer>
  );
};

TableItemsContainerComponent.propTypes = {
  layout: PropTypes.string,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.elementType
};

const TableListBrowser = ({ name, store, ItemComponent, columns, layout = 'fixed', minWidth, ...rest }) => {
  const { t } = useTranslation();
  const { textResolver } = useListBrowserFunctions();

  const TableListHeaderComponent = useCallback(
    () => (
      <TableHead>
        <RoundedHeaderTableRow>
          {columns.map((column, index) => {
            let width, align;

            if (column.width) {
              if (!isNaN(column.width)) width = `${column.width}%`;
              else width = column.width;
            }

            if (column.align) align = column.align;
            else {
              if (index + 1 === columns.length) align = 'right';
              else align = 'left';
            }

            return (
              <ThemedHeaderTableCell key={column.name} title={t(column.caption)} width={width} align={align}>
                {t(column.caption)}
              </ThemedHeaderTableCell>
            );
          })}
        </RoundedHeaderTableRow>
      </TableHead>
    ),
    [columns, t]
  );

  const TableLoadingComponent = useCallback(
    () => (
      <TableRow>
        <LoadingTableCell colSpan={columns.length}>
          <FakeText lines={10} />
        </LoadingTableCell>
      </TableRow>
    ),
    [columns]
  );

  const TableEmptyListComponent = useCallback(
    () => (
      <TableRow>
        <TableCell colSpan={columns.length}>
          <Typography variant='subtitle2' fontStyle='italic' color='textPrimary' textAlign='center'>
            {t('ListBrowser.NoEntries')}
          </Typography>
        </TableCell>
      </TableRow>
    ),
    [columns, t]
  );

  const sanitizedRest = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const { ItemsComponent, ...others } = rest;
    return others;
  }, [rest]);

  const itemsContainerComponent = useCallback(
    ({ children }) => {
      const internalMinWidth = minWidth || `${columns.length * 125}px`;
      return (
        <TableItemsContainerComponent layout={layout} minWidth={internalMinWidth}>
          {children}
        </TableItemsContainerComponent>
      );
    },
    [layout, minWidth, columns]
  );

  return (
    <ListBrowser
      name={name}
      store={store}
      ItemComponent={ItemComponent}
      ItemsContainerComponent={itemsContainerComponent}
      ListHeaderComponent={TableListHeaderComponent}
      LoadingComponent={TableLoadingComponent}
      EmptyListComponent={TableEmptyListComponent}
      textResolver={textResolver}
      {...sanitizedRest}
    />
  );
};

TableListBrowser.propTypes = {
  name: PropTypes.string,
  store: PropTypes.object,
  ItemComponent: PropTypes.elementType,
  columns: PropTypes.array,
  layout: PropTypes.string,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default TableListBrowser;
